import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { StorefrontHeader } from '../components/storefront/StorefrontHeader';
import { useCartStore, CartItem } from '../store/cartStore';
import { useStorefront } from '../hooks/useStorefront';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, typography } from '../theme';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { getCurrencySymbol } from '../utils/currencies';
import { useOrderMinimums } from '../hooks/useOrderMinimums';
import { OrderMinimumsNotice } from '../components/OrderMinimumsNotice';
import { businessCurrency } from '../utils/businessDefaults';

const formatMoney = (currency: string, amount: number) => `${currency} ${Number(amount || 0).toFixed(2)}`;

const itemImage = (item: CartItem): string | undefined => {
  if (item.product.primaryImage) return item.product.primaryImage;
  const images = item.product.images || [];
  return images.find((i) => i.isPrimary)?.url || images[0]?.url;
};

export const unitPriceOf = (item: CartItem) => {
  const base = item.variant?.price ?? item.product.salePrice ?? item.product.price ?? 0;
  const addons = (item.selectedAddons || []).reduce((s, a) => s + a.price * a.quantity, 0);
  return base + addons;
};

const stockLimitOf = (item: CartItem) => {
  if (item.variant?.inventoryQuantity !== undefined) return item.variant.inventoryQuantity;
  if (item.product.inventoryTracking && item.product.inventoryQuantity !== undefined) return item.product.inventoryQuantity;
  return Infinity;
};

export const CartScreen: React.FC = () => {
  const s = useThemedRTLStyles(getStyles);
  const { t, isRTL } = useLocale();
  const insets = useSafeAreaInsets();
  const { params, go } = useStorefront();
  const { items, totalItems, totalPrice, updateQuantity, removeItem, shared } = useCartStore();
  // Opened from a conversation: its shared cart, priced with taxes and charges by the server.
  const sharedHere = shared && params.conversationId && shared.conversationId === params.conversationId ? shared : undefined;
  const priced = sharedHere?.priced && sharedHere.priced.subtotal > 0 ? sharedHere.priced : undefined;
  const [deciding, setDeciding] = useState(false);
  const decide = async (decision: 'continue' | 'new') => {
    if (!sharedHere?.otherCart || !params.businessId || !params.conversationId || !params.ct) return;
    setDeciding(true);
    try {
      const { decideSharedCart } = await import('../store/sharedCartSync');
      await decideSharedCart({ businessId: params.businessId, conversationId: params.conversationId, ct: params.ct }, sharedHere.otherCart.conversationId, decision);
    } catch { /* the banner stays; they can try again */ } finally { setDeciding(false); }
  };
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);

  useEffect(() => {
    getApiBaseURL().then(setApiBaseURL).catch(() => {});
  }, []);

  const backToShop = () => {
    if (params.catalogId) go('catalog-view');
    else if (params.productId) go('product-view');
    else if (Platform.OS === 'web' && typeof window !== 'undefined' && window.history.length > 1) window.history.back();
  };

  const currency = getCurrencySymbol(items[0]?.product.currency || businessCurrency());

  // Product minimum quantities + vendor minimum order amounts.
  const minimumsLines = useMemo(
    () => items.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity, lineTotal: unitPriceOf(i) * i.quantity })),
    [items]
  );
  const { report: minimums, ok: minimumsOk } = useOrderMinimums(params.businessId, minimumsLines, items[0]?.product.currency);
  const productQty = (productId: string) => items.filter((i) => i.productId === productId).reduce((sum, i) => sum + i.quantity, 0);

  if (items.length === 0) {
    return (
      <View style={s.screen}>
        <StorefrontHeader title={t('storefront.cart.title')} onBack={backToShop} />
        <View style={s.center}>
          <View style={s.stateIcon}>
            <Ionicons name="bag-outline" size={38} color={colors.text.secondary as string} />
          </View>
          <Text style={s.stateTitle}>{t('storefront.cart.empty')}</Text>
          <Text style={s.stateBody}>{t('storefront.cart.emptyBody')}</Text>
          {(params.catalogId || params.productId) ? (
            <TouchableOpacity style={s.primaryBtn} onPress={backToShop} activeOpacity={0.85}>
              <Ionicons name="storefront-outline" size={18} color={colors.text.onPrimary as string} />
              <Text style={s.primaryBtnText}>{t('storefront.cart.startShopping')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  }

  return (
    <View style={s.screen}>
      <StorefrontHeader
        title={t('storefront.cart.title')}
        subtitle={totalItems === 1 ? t('storefront.oneItem') : t('storefront.itemsCount', { count: totalItems })}
        onBack={backToShop}
      />

      <ScrollView style={{ flex: 1 }} contentContainerStyle={s.listContent} showsVerticalScrollIndicator={false}>
        <View style={s.column}>
          {items.map((item, index) => {
            const img = itemImage(item);
            const unit = unitPriceOf(item);
            const atLimit = item.quantity >= stockLimitOf(item);
            return (
              <View key={`${item.productId}_${item.variantId ?? 'nv'}_${index}`} style={s.card}>
                <View style={s.imageBox}>
                  {img ? (
                    Platform.OS === 'web' ? (
                      <img
                        src={getImageUrlSync(img, apiBaseURL)}
                        alt={item.product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' } as any}
                      />
                    ) : (
                      <Image source={{ uri: getImageUrlSync(img, apiBaseURL) }} style={s.image} resizeMode="cover" />
                    )
                  ) : (
                    <View style={s.imagePlaceholder}>
                      <Ionicons name="image-outline" size={24} color={colors.text.disabled as string} />
                    </View>
                  )}
                </View>

                <View style={s.info}>
                  <View style={s.nameRow}>
                    <Text style={s.name} numberOfLines={2}>{item.product.name}</Text>
                    <TouchableOpacity
                      onPress={() => removeItem(item.productId, item.variantId)}
                      hitSlop={10}
                      accessibilityLabel={t('storefront.cart.remove')}
                    >
                      <Ionicons name="close" size={18} color={colors.text.secondary as string} />
                    </TouchableOpacity>
                  </View>
                  {item.variant ? <Text style={s.meta} numberOfLines={1}>{item.variant.name}</Text> : null}
                  {(item.product.minimumOrderQuantity || 0) > 1 ? (
                    <Text style={s.minQty}>{t('storefront.minQty', { count: item.product.minimumOrderQuantity! })}</Text>
                  ) : null}
                  {item.selectedAddons?.length ? (
                    <Text style={s.meta} numberOfLines={2}>
                      + {item.selectedAddons.map((a) => (a.quantity > 1 ? `${a.name} ×${a.quantity}` : a.name)).join(isRTL ? '، ' : ', ')}
                    </Text>
                  ) : null}

                  <View style={s.bottomRow}>
                    <View style={s.qtyRow}>
                      <TouchableOpacity
                        style={s.qtyBtn}
                        hitSlop={6}
                        onPress={() => {
                          // Dropping below the product's minimum removes the line.
                          const min = Math.max(1, item.product.minimumOrderQuantity || 1);
                          if (item.quantity > 1 && productQty(item.productId) - 1 >= min) updateQuantity(item.productId, item.variantId, item.quantity - 1);
                          else removeItem(item.productId, item.variantId);
                        }}
                      >
                        <Ionicons
                          name={item.quantity === 1 || productQty(item.productId) <= Math.max(1, item.product.minimumOrderQuantity || 1) ? 'trash-outline' : 'remove'}
                          size={16}
                          color={colors.primary as string}
                        />
                      </TouchableOpacity>
                      <Text style={s.qtyText}>{item.quantity}</Text>
                      <TouchableOpacity
                        style={[s.qtyBtn, atLimit && { opacity: 0.35 }]}
                        hitSlop={6}
                        disabled={atLimit}
                        onPress={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                      >
                        <Ionicons name="add" size={16} color={colors.primary as string} />
                      </TouchableOpacity>
                    </View>
                    <View style={s.priceBlock}>
                      <Text style={s.lineTotal}>{formatMoney(currency, unit * item.quantity)}</Text>
                      {item.quantity > 1 ? (
                        <Text style={s.unitPrice}>{t('storefront.cart.each', { price: formatMoney(currency, unit) })}</Text>
                      ) : null}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}

          {(params.catalogId || params.productId) ? (
            <TouchableOpacity style={s.continueBtn} onPress={backToShop}>
              <Ionicons name="add-circle-outline" size={18} color={colors.primary as string} />
              <Text style={s.continueText}>{t('storefront.cart.continueShopping')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </ScrollView>

      <View style={[s.summary, { paddingBottom: 14 + insets.bottom }]}>
        <View style={s.column}>
          <OrderMinimumsNotice report={minimums} style={{ marginBottom: 12 }} />
          {!!sharedHere?.otherCart && !items.length && (
            <View style={s.otherCart}>
              <Text style={s.otherCartText}>{t('storefront.cart.otherNumber', { count: sharedHere.otherCart.items, total: formatMoney(getCurrencySymbol(sharedHere.otherCart.currency || businessCurrency()), sharedHere.otherCart.total) })}</Text>
              <View style={s.otherCartActions}>
                <TouchableOpacity style={[s.otherCartBtn, s.otherCartPrimary]} disabled={deciding} onPress={() => decide('continue')}>
                  <Text style={[s.otherCartBtnText, { color: colors.text.onPrimary as string }]}>{t('storefront.cart.continueHere')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={s.otherCartBtn} disabled={deciding} onPress={() => decide('new')}>
                  <Text style={s.otherCartBtnText}>{t('storefront.cart.startNew')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
          <View style={s.summaryRow}>
            <Text style={s.summaryLabel}>{t('storefront.cart.subtotal')}</Text>
            <Text style={s.summaryValue}>{formatMoney(currency, priced ? priced.subtotal : totalPrice)}</Text>
          </View>
          {priced ? (
            <>
              {priced.taxes.map((x) => (
                <View key={`t-${x.name}`} style={s.summaryRow}>
                  <Text style={s.feesNote}>{x.name} ({x.rate}%)</Text>
                  <Text style={s.feesNote}>{formatMoney(currency, x.amount)}</Text>
                </View>
              ))}
              {priced.charges.map((x) => (
                <View key={`c-${x.name}`} style={s.summaryRow}>
                  <Text style={s.feesNote}>{x.name}</Text>
                  <Text style={s.feesNote}>{formatMoney(currency, x.amount)}</Text>
                </View>
              ))}
              {(priced.taxes.length > 0 || priced.charges.length > 0) && (
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>{t('storefront.cart.total')}</Text>
                  <Text style={s.summaryValue}>{formatMoney(currency, priced.total)}</Text>
                </View>
              )}
              <Text style={s.feesNote}>{t('storefront.cart.deliveryNote')}</Text>
            </>
          ) : <Text style={s.feesNote}>{t('storefront.cart.feesNote')}</Text>}
          <TouchableOpacity
            style={[s.checkoutBtn, !minimumsOk && { opacity: 0.45 }]}
            onPress={() => minimumsOk && go('checkout')}
            disabled={!minimumsOk}
            activeOpacity={0.88}
          >
            <Text style={s.checkoutText}>{t('storefront.cart.checkout')}</Text>
            <DirectionalIcon name="arrow-forward" size={18} color={colors.text.onPrimary as string} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background.secondary,
      ...Platform.select({
        web: { width: '100%', maxWidth: '100%', overflowX: 'hidden' as any, height: '100dvh' as any, maxHeight: '100dvh' as any, minHeight: 0, display: 'flex' as any, flexDirection: 'column' as any },
      }),
    } as any,
    column: { width: '100%', maxWidth: 680, alignSelf: 'center' },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 10 },
    stateIcon: {
      width: 84,
      height: 84,
      borderRadius: 42,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: 6,
    },
    stateTitle: { fontSize: 19, fontFamily: typography.fontFamily.bold, color: colors.text.primary, textAlign: 'center' },
    stateBody: { fontSize: 14, lineHeight: 21, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, textAlign: 'center', maxWidth: 340 },
    primaryBtn: {
      marginTop: 12,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      backgroundColor: colors.primary,
      borderRadius: 14,
      paddingHorizontal: 22,
      paddingVertical: 13,
    },
    primaryBtnText: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },

    listContent: { padding: 14, paddingBottom: 24 },
    card: {
      flexDirection: 'row',
      gap: 12,
      padding: 12,
      marginBottom: 10,
      borderRadius: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    imageBox: { width: 76, height: 76, borderRadius: 12, overflow: 'hidden', backgroundColor: colors.background.secondary },
    image: { width: '100%', height: '100%' },
    imagePlaceholder: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    info: { flex: 1, minWidth: 0, gap: 3 },
    nameRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
    name: { flex: 1, fontSize: 14, lineHeight: 20, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    meta: { fontSize: 12, lineHeight: 17, fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
    minQty: { fontSize: 11, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    bottomRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, gap: 8 },
    qtyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 34,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: colors.primary,
      backgroundColor: colors.primaryTransparent,
    },
    qtyBtn: { width: 34, height: 32, alignItems: 'center', justifyContent: 'center' },
    qtyText: { minWidth: 26, fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.primary, textAlign: 'center' },
    priceBlock: { alignItems: 'flex-end' },
    lineTotal: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    unitPrice: { fontSize: 11, fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
    continueBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12 },
    continueText: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.primary },

    summary: {
      backgroundColor: colors.background.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
      paddingHorizontal: 16,
      paddingTop: 14,
    },
    summaryRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    otherCart: { padding: 12, marginBottom: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.primary, gap: 10 },
    otherCartText: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    otherCartActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    otherCartBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, borderWidth: 1, borderColor: colors.primary },
    otherCartPrimary: { backgroundColor: colors.primary },
    otherCartBtnText: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    summaryLabel: { fontSize: 15, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    summaryValue: { fontSize: 18, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    feesNote: { fontSize: 12, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, marginTop: 4 },
    checkoutBtn: {
      marginTop: 12,
      height: 52,
      borderRadius: 14,
      backgroundColor: colors.primary,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    checkoutText: { fontSize: 16, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
  });
